import { sortBy } from 'remeda'
import { z } from 'zod/v4'

import type { ReferenceInput } from '@/types'
import type {
  GroupedOptions,
  IndexedReference,
  Options,
} from './reference.type'
import { formulaOverride } from '@/utils/formula.helpers'
import { groupByToArray } from '@/utils/groupBy'

// ---------------------------------------------------------------------------
// Indexing helpers
// ---------------------------------------------------------------------------

/**
 * Flatten properties from `Array<{ key, values }>` to `Record<key, values>`
 * for O(1) field access.
 */
const toPropertiesMap = (ref: ReferenceInput): Record<string, string[]> => {
  const map: Record<string, string[]> = {}
  for (const prop of ref.properties ?? []) {
    if (prop.key && prop.values && prop.values.length > 0) {
      map[prop.key] = prop.values
    }
  }
  return map
}

/**
 * Index an array of raw references into a `Record<id, IndexedReference>`.
 * Properties are flattened from the array format to a key→values map
 * for direct O(1) access by field name.
 */
export const indexReferencesById = (
  references: ReferenceInput[],
): Record<string, IndexedReference> => {
  const map: Record<string, IndexedReference> = {}
  for (const ref of references) {
    map[ref.id] = {
      id: ref.id,
      byUserID: ref.byUserID,
      organizationID: ref.organizationID,
      organizationMasterID: ref.organizationMasterID,
      status: ref.status,
      properties: toPropertiesMap(ref),
    }
  }
  return map
}

/**
 * Resolve the label for the first value of a LIST-type property
 * by looking it up in the refData (id → label) map.
 */
const resolveLabel = (
  values: string[] | undefined,
  refData: Record<string, string> | undefined,
): string | undefined => {
  if (!values?.length || !refData) return undefined
  return refData[values[0]]
}

type RefOptionsParams = {
  references: ReferenceInput[]
  refKey: string
  refDataByKey: Partial<Record<string, Record<string, string>>>
  nameOptionsById?: Record<string, { status?: string }>
  unavailableNameLabel?: string
  audience?: 'picker' | 'display'
}

export const referenceToOptions = ({
  references,
  refKey,
  refDataByKey,
  nameOptionsById,
  unavailableNameLabel = 'Label manquant',
  audience = 'picker',
}: RefOptionsParams): Options[] | GroupedOptions[] => {
  let hasCategory = false

  try {
    const result = references
      .filter(
        (ref) =>
          audience === 'display' ||
          (ref.status !== 'ARCHIVED' && ref.status !== 'REJECTED'),
      )
      .map((ref): Options => {
        const props = toPropertiesMap(ref)
        const nameOptionId = props['name']?.[0]

        // Resolve labels for LIST-type fields via refDataByKey
        const resolvedName = resolveLabel(props['name'], refDataByKey['name'])
        const nameLabel = resolvedName || unavailableNameLabel
        const categoryLabel = resolveLabel(
          props['category'],
          refDataByKey['category'],
        )
        const materialLabel = resolveLabel(
          props['material'],
          refDataByKey['material'],
        )

        hasCategory = hasCategory || !!categoryLabel

        // Raw TEXT-type fields: read directly from properties
        const refValue = props[refKey]?.[0]
        const refIsUUID = z.uuid().safeParse(refValue).success

        const displayMaterial =
          (props['display_material']?.[0] === 'true' ||
            props['custom_boolean_1']?.[0] === 'true') &&
          materialLabel

        const cleanRef = formulaOverride(refValue) ?? refValue
        const displayLabel =
          refValue && !refIsUUID && refValue !== nameLabel
            ? `${cleanRef} - ${nameLabel}${displayMaterial ? ` ${materialLabel}` : ''}`
            : `${nameLabel}${displayMaterial ? ` ${materialLabel}` : ''}`

        return {
          label: displayLabel,
          value: ref.id,
          category: categoryLabel || 'Non catégorisé',
          id: ref.id,
          rotorOrder: 0,
          status: ref.status,
          nameLabel: resolvedName,
          nameOptionStatus: nameOptionId
            ? nameOptionsById?.[nameOptionId]?.status
            : undefined,
          details: [categoryLabel, materialLabel].filter(Boolean).join(' · '),
          isReference: true,
          isInReview: ref.status === 'PENDING',
          isSelectable:
            Boolean(resolvedName) &&
            ref.status !== 'ARCHIVED' &&
            ref.status !== 'REJECTED',
        }
      })

    const sortedResult = sortBy(result, (item) => item.label)
    if (hasCategory) {
      const groupedResult = groupByToArray(
        sortedResult,
        (item) => item.category ?? 'Non catégorisé',
      )
      return sortBy(groupedResult, (item) => item.label) as GroupedOptions[]
    }

    return sortedResult
  } catch (error) {
    console.error('referenceToOptions error:', error)
    return []
  }
}
